import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Heart,
  MessageCircle,
  Send,
  Smile,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useParams, useSearchParams } from "react-router-dom";
import { SEO } from "../components/common/SEO";
import { Button } from "../components/ui/Button";
import { config } from "../utils/config";

type ShareType = "post" | "video";

const shareTypeByPath: Record<string, ShareType> = {
  reels: "video",
  feed: "post",
};

const supportedSharePaths = new Set(["reels", "feed", "profile", "live", "pk", "audio"]);

type SharedComment = {
  id?: string;
  author: string;
  avatar?: string | null;
  text: string;
  createdAt?: string;
  likes?: number;
};

type SharedMediaItem = {
  url: string;
  isBanned?: boolean;
};

type SharedContent = {
  id: string;
  type: ShareType;
  caption: string;
  mediaUrl: string | null;
  mediaItems: SharedMediaItem[];
  hlsUrl: string | null;
  videoUrl: string | null;
  posterUrl: string | null;
  likes: number;
  comments: number;
  shares: number;
  author: string;
  authorAvatar: string | null;
  createdAt?: string;
  postedAt?: string;
  publishedAt?: string;
  postDate?: string;
  hashtags?: string[] | string;
  tags?: string[] | string;
  commentsList?: SharedComment[];
};

const fallbackAvatar = "https://www.swagshots.com/assets/logo-128.webp";

export function SharePage() {
  const [params] = useSearchParams();
  const { shareKind, shareId } = useParams();
  const pathType = shareKind ? shareTypeByPath[shareKind] : undefined;
  const type: ShareType = pathType || (params.get("type") === "video" ? "video" : "post");
  const id = shareId || params.get("id") || params.get("postId") || "";
  const resolvedShareKind = shareKind || (type === "video" ? "reels" : "feed");
  const sharePath = `${resolvedShareKind}/${encodeURIComponent(id)}`;
  const shareUrl = `${config.WEB_BASE_URL}/share/${sharePath}`;
  const [content, setContent] = useState<SharedContent | null>(null);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (shareKind && !supportedSharePaths.has(shareKind)) {
      setError("This share link type is not supported.");
      return;
    }

    if (!id) {
      setError("This share link is missing a content ID.");
      return;
    }

    const endpoint = `${config.API_BASE_URL}/client/share/${sharePath}`;

    fetch(endpoint)
      .then(async (response) => {
        const result = await response.json();
        if (!response.ok || !result.status) throw new Error(result.message || "Content not found.");

        const data = result.data as SharedContent & {
          comments?: SharedComment[] | number;
          commentList?: SharedComment[];
          commentsList?: SharedComment[];
        };

        return {
          ...data,
          mediaItems:
            Array.isArray(data.mediaItems) && data.mediaItems.length
              ? data.mediaItems
              : data.mediaUrl
                ? [{ url: data.mediaUrl }]
                : [],
          commentsList: Array.isArray(data.commentsList)
            ? data.commentsList
            : Array.isArray(data.commentList)
              ? data.commentList
              : Array.isArray(data.comments)
                ? data.comments
                : [],
        } as SharedContent;
      })
      .then(setContent)
      .catch((value) => setError(value instanceof Error ? value.message : "Unable to load this share."));
  }, [id, shareKind, sharePath]);

  useEffect(() => {
    setActiveMediaIndex(0);
  }, [content?.id]);

  const togglePlayback = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) void videoRef.current.play();
    else videoRef.current.pause();
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const openApp = () => {
    const link = `swagshots://share/${sharePath}`;
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent) || /Macintosh/i.test(navigator.userAgent) && "ontouchend" in document;
    const isAndroid = /Android/i.test(navigator.userAgent);
    const storeUrl = isIOS ? config.IOS_APP_STORE_URL : config.ANDROID_PLAY_STORE_URL;

    if (isIOS || isAndroid) {
      window.location.href = link;
      window.setTimeout(() => {
        window.location.href = storeUrl;
      }, 1800);
      return;
    }

    window.location.href = storeUrl;
  };

  const title = type === "video" ? "Shared video" : "Shared post";
  const avatar = content?.authorAvatar || fallbackAvatar;
  const comments = content?.commentsList || [];
  const postMedia = (content?.mediaItems || []).filter((item) => item.url && !item.isBanned);
  const activePostMedia = postMedia[activeMediaIndex] || postMedia[0];
  const metadata = content ? getContentMetadata(content) : "";
  const hashtags = content ? formatHashtags(content.hashtags ?? content.tags) : "";

  if (error) {
    return (
      <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0d1014] px-5 text-white">
        <SEO
          title={title}
          description={`${title} on Swag Shots.`}
          image={fallbackAvatar}
          url={shareUrl}
          type="article"
        />
        <section className="relative w-full max-w-md rounded-[2rem] border border-white/10 bg-[#1f2227] p-8 text-center shadow-2xl sm:p-10">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500/15 text-orange-300">
            <AlertCircle size={32} strokeWidth={1.8} />
          </div>
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-orange-300">Share unavailable</p>
          <h1 className="mt-3 text-2xl font-bold tracking-tight text-white">We couldn’t load this {type}.</h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-white/60">
            This share may have expired, been removed, or is temporarily unavailable. Please try again in a moment.
          </p>
          <p className="mt-4 truncate text-xs text-white/35" title={error}>
            {error}
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button className="rounded-full px-6" onClick={() => window.location.reload()}>
              Try again
            </Button>
            <a
              href="/"
              className="rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold text-white/80 transition hover:bg-white/10"
            >
              Go home
            </a>
          </div>
        </section>
      </main>
    );
  }

  if (!content) return <ShareSkeleton type={type} title={title} onOpenApp={openApp} />;

  return (
    <main className="min-h-screen bg-white pt-16 text-white">
      <SEO
        title={title}
        description={`${content.caption || title} on Swag Shots.`}
        image={content.posterUrl || content.mediaUrl || fallbackAvatar}
        url={shareUrl}
        type="article"
      />
      <header className="fixed inset-x-0 top-0 z-20 border-b border-black/10 bg-white shadow-sm">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 md:px-8">
          <a href="/" aria-label="Swag Shots home" className="flex items-center gap-2">
            <img src="/assets/logo-64.webp" alt="Swag Shots" className="h-9 w-9 rounded-xl" />
            <span className="text-lg font-bold tracking-tight text-gray-900">Swag Shots</span>
          </a>
          <Button size="sm" className="rounded-full px-5" onClick={openApp}>
            Open in app
          </Button>
        </div>
      </header>

      <section className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-4 md:px-8 md:py-8">
        <article className="grid w-full max-w-6xl overflow-hidden border border-black/10 bg-white shadow-2xl lg:h-[calc(100vh-8rem)] lg:min-h-0 lg:max-h-none lg:grid-cols-2">
          <div className="bg-black">
            {type === "video" ? (
              <div className="relative h-full min-h-[760px] lg:min-h-0">
                <video
                  ref={videoRef}
                  className="h-full w-full object-cover"
                  src={content.videoUrl || content.hlsUrl || undefined}
                  poster={content.posterUrl || undefined}
                  controls={false}
                  autoPlay
                  muted
                  playsInline
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onClick={togglePlayback}
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute inset-x-4 bottom-5 pr-16">
                  <div className="mb-3 flex items-center gap-3">
                    <img
                      src={avatar}
                      alt={content.author}
                      className="h-10 w-10 rounded-full border border-grey/80 object-cover"
                    />
                    <strong>{content.author}</strong>
                    <span className="text-white/60">•</span>
                    <button
                      onClick={openApp}
                      className="pointer-events-auto text-sm font-semibold text-white hover:text-orange-300"
                    >
                      Open app
                    </button>
                  </div>
                </div>
                <button
                  onClick={toggleMute}
                  aria-label={muted ? "Unmute video" : "Mute video"}
                  className="absolute bottom-8 right-3 rounded-full p-2 text-white transition hover:bg-white/15"
                >
                  {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
                </button>
                {!playing && (
                  <button
                    onClick={togglePlayback}
                    aria-label="Play video"
                    className="absolute inset-0 m-auto h-16 w-16 rounded-full bg-white/90 text-2xl text-black shadow-xl"
                  >
                    ▶
                  </button>
                )}
              </div>
            ) : (
              <div className="relative h-full min-h-[760px] lg:min-h-0">
                <img
                  src={activePostMedia?.url || undefined}
                  alt={content.caption || title}
                  className="h-full w-full object-contain"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute inset-x-4 bottom-5 pr-16">
                  <div className="mb-3 flex items-center gap-3">
                    <img
                      src={avatar}
                      alt={content.author}
                      className="h-10 w-10 rounded-full border border-grey/0 object-cover"
                    />
                    <strong>{content.author}</strong>
                    <span className="text-white/60">•</span>
                    <button
                      onClick={openApp}
                      className="pointer-events-auto text-sm font-semibold text-white hover:text-orange-300"
                    >
                      Open app
                    </button>
                  </div>
                </div>
                {/* <div className="absolute bottom-6 right-3 flex flex-col items-center gap-5">
                  <Action icon={<Heart />} value={content.likes} dark />
                  <Action icon={<MessageCircle />} value={content.comments} dark />
                  <Action icon={<Send />} value={content.shares} dark />
                </div> */}
                {postMedia.length > 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveMediaIndex((index) => Math.max(index - 1, 0))
                      }
                      disabled={activeMediaIndex === 0}
                      aria-label="Previous image"
                      className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ChevronLeft size={22} strokeWidth={2.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveMediaIndex((index) => Math.min(index + 1, postMedia.length - 1))}
                      disabled={activeMediaIndex === postMedia.length - 1}
                      aria-label="Next image"
                      className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ChevronRight size={22} strokeWidth={2.5} />
                    </button>
                    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/50 px-3 py-2">
                      {postMedia.map((item, index) => (
                        <button
                          type="button"
                          key={item.url}
                          onClick={() => setActiveMediaIndex(index)}
                          aria-label={`Show image ${index + 1}`}
                          className={`h-2 w-2 rounded-full ${
                            index === activeMediaIndex ? "bg-white" : "bg-white/45"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>

          <aside className="flex min-h-0 flex-col bg-white">
            <header className="flex items-center justify-between border-b border-black/10 px-5 py-4">
              <div className="flex items-center gap-3">
                <img src={avatar} alt={content.author} className="h-10 w-10 rounded-full object-cover" />
                <div>
                  <strong className="text-gray-900">{content.author}</strong>
                  {metadata ? <p className="text-xs text-gray-500">{metadata}</p> : null}
                  <p className="text-xs text-gray-500">Read-only comments</p>
                </div>
              </div>
              <button
                onClick={openApp}
                className="rounded-full border border-black/10 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-black/5"
              >
                Open app
              </button>
            </header>

            {content.caption || hashtags ? (
              <div className="border-b border-black/10 px-5 py-4">
                {content.caption ? <p className="text-sm leading-6 text-gray-700">{content.caption}</p> : null}
                {hashtags ? <p className="mt-2 text-sm font-medium leading-6 text-sky-600">{hashtags}</p> : null}
              </div>
            ) : null}

            <div className="flex items-center gap-5 border-b border-black/10 px-5 py-4">
              <Action icon={<Heart />} value={content.likes} />
              <Action icon={<MessageCircle />} value={content.comments} />
              <Action icon={<Send />} value={content.shares} />
            </div>

            <div className="flex items-center justify-between border-b border-black/10 px-5 py-3 text-xs uppercase tracking-[0.2em] text-gray-500">
              <span>Comments</span>
              <span>{comments.length ? `${comments.length} comments` : "No comments yet"}</span>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {comments.length ? (
                <div className="space-y-4">
                  {comments.map((comment, index) => (
                    <CommentRow key={comment.id || `${comment.author}-${index}`} comment={comment} />
                  ))}
                </div>
              ) : (
                <div className="flex h-full min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-black/10 px-6 text-center">
                  <div className="mb-4 rounded-full bg-black/5 p-3">
                    <MessageCircle size={22} className="text-gray-500" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Be the first to comment</h3>
                  <p className="mt-2 max-w-sm text-sm leading-6 text-gray-500">
                    Share your thoughts on this {type === "video" ? "video" : "post"}.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-black/10 px-5 py-4">
              <div className="flex items-center gap-3 rounded-full border border-black/10 bg-black/[0.03] px-4 py-3 text-sm text-gray-500">
                <Smile size={18} />
                <span>Log in to like or comment.</span>
              </div>
              <Button className="mt-4 w-full rounded-full" onClick={openApp}>
                Open in Swag Shots
              </Button>
            </div>
          </aside>
        </article>
      </section>
    </main>
  );
}

function Action({ icon, value, dark }: { icon: React.ReactNode; value?: number; dark?: boolean }) {
  return (
    <button
      className={`flex flex-col items-center gap-1 text-sm transition hover:scale-105 ${
        dark ? "text-white" : "text-gray-900"
      }`}
    >
      {icon}
      <span className="text-xs">{value ?? ""}</span>
    </button>
  );
}

function CommentRow({ comment }: { comment: SharedComment }) {
  const avatar = comment.avatar || fallbackAvatar;
  const time = comment.createdAt ? formatRelativeTime(comment.createdAt) : "";

  return (
    <div className="flex gap-3">
      <img src={avatar} alt={comment.author} className="h-10 w-10 rounded-full object-cover" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <strong className="text-sm text-gray-900">{comment.author}</strong>
          {time ? <span className="text-xs text-gray-500">{time}</span> : null}
        </div>
        <p className="mt-1 text-sm leading-6 text-gray-700">{comment.text}</p>
        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
          <button className="transition hover:text-gray-900">Like</button>
          <button className="transition hover:text-gray-900">Reply</button>
          {typeof comment.likes === "number" ? <span>{comment.likes} likes</span> : null}
        </div>
      </div>
    </div>
  );
}

function getContentMetadata(content: SharedContent) {
  const dateValue = content.postDate || content.publishedAt || content.postedAt || content.createdAt;
  const date = dateValue ? formatPostDate(dateValue) : "";

  return date;
}

function formatHashtags(value?: string[] | string) {
  if (!value) return "";

  return (Array.isArray(value) ? value : value.split(/[\s,]+/))
    .filter(Boolean)
    .map((tag) => (tag.startsWith("#") ? tag : `#${tag}`))
    .join(" ");
}

function formatPostDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatRelativeTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diff = Date.now() - date.getTime();
  const minutes = Math.max(1, Math.round(diff / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}

function ShareSkeleton({ type, title, onOpenApp }: { type: ShareType; title: string; onOpenApp: () => void }) {
  const header = (
    <header className="fixed inset-x-0 top-0 z-20 border-b border-black/10 bg-white shadow-sm">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 md:px-8">
        <a href="/" aria-label="Swag Shots home" className="flex items-center gap-2">
          <img src="/assets/logo-64.webp" alt="Swag Shots" className="h-9 w-9 rounded-xl" />
          <span className="text-lg font-bold tracking-tight text-gray-900">Swag Shots</span>
        </a>
        <Button size="sm" className="rounded-full px-5" onClick={onOpenApp}>
          Open in app
        </Button>
      </div>
    </header>
  );

  if (type === "video")
    return (
      <main className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-white px-3 py-2 md:py-4">
        {header}
        <div className="relative h-[calc(100vh-8rem)] max-h-[980px] w-full max-w-[576px] animate-pulse rounded-xl bg-[#171b21] shadow-2xl md:h-[calc(100vh-8.5rem)] md:max-h-none">
          <div className="absolute inset-x-5 bottom-8 space-y-4">
            <div className="h-10 w-40 rounded-full bg-white/10" />
            <div className="h-3 w-4/5 rounded bg-white/10" />
            <div className="h-3 w-3/5 rounded bg-white/10" />
          </div>
          <div className="absolute bottom-8 right-4 space-y-5">
            <div className="h-10 w-10 rounded-full bg-white/10" />
            <div className="h-10 w-10 rounded-full bg-white/10" />
            <div className="h-10 w-10 rounded-full bg-white/10" />
          </div>
          <span className="sr-only">Loading {title}</span>
        </div>
      </main>
    );

  return (
    <main className="flex min-h-screen items-center justify-center bg-white px-4 pb-12 pt-20">
      {header}
      <div className="relative w-full max-w-[576px] animate-pulse overflow-hidden rounded-xl bg-[#171b21] shadow-2xl">
        <div className="h-[calc(100vh-8.5rem)] min-h-[700px] w-full md:min-h-0" />
        <div className="absolute inset-x-5 bottom-8 space-y-4">
          <div className="h-10 w-40 rounded-full bg-white/10" />
          <div className="h-3 w-4/5 rounded bg-white/10" />
          <div className="h-3 w-3/5 rounded bg-white/10" />
        </div>
        <div className="absolute bottom-8 right-4 space-y-5">
          <div className="h-10 w-10 rounded-full bg-white/10" />
          <div className="h-10 w-10 rounded-full bg-white/10" />
          <div className="h-10 w-10 rounded-full bg-white/10" />
        </div>
        <span className="sr-only">Loading {title}</span>
      </div>
    </main>
  );
}
